import { createClient } from '@supabase/supabase-js';
import type { IUser } from './IUser';
import { generateRoundRobinSchedule, RoundRobinPlayer, BYE_USER_ID } from './tournaments/RoundRobinScheduler';
import { calculateStandings, DEFAULT_TIEBREAKERS } from './tournaments/RoundRobinStandings';
import { installFetchInterceptorEarly } from './SupabaseInterceptor';

// CRITICAL: Install fetch interceptor BEFORE creating Supabase client
// so the client uses the wrapped fetch instead of capturing the original
installFetchInterceptorEarly();

const url = process.env.REACT_APP_SUPA_URL;
const anon = process.env.REACT_APP_SUPA_KEY;

export const sb = createClient(url, anon);


export type UserRow = {
  id: string;
  user_name: string;
  wins: number; losses: number; num_of_skeeches: number;
  points_total: number; rolls_total: number; rounds_total: number; games_played: number;
  avg_ppg: number; avg_ppr: number; avg_rpg: number;
  created_at: string; updated_at: string;
  profile_image_url: string | null;
};

export type GameRow = {
  id: string;
  is_solo_game: boolean;
  p1_user_id: string | null;
  p2_user_id: string | null;
  p1_score: number;
  p2_score: number | null;
  rounds: number; rolls: number; points: number;
  skeeches: number;
  time_stamp: string;
  status: 'active' | 'completed' | 'forfeited';
  game_state_json: any;
  updated_at: string;
  p1_roll_history: number[] | null; // Player 1's individual roll values
  p2_roll_history: number[] | null; // Player 2's individual roll values
  winner_id?: string | null; // User ID of winner (set for completed and forfeited games)
  // Tournament context (optional)
  tournament_id?: string | null;
  tournament_round?: number | null;
  tournament_match?: number | null;
};

export type TournamentRow = {
  id: string;
  name: string;
  join_code: string;
  creator_id: string;
  max_players: number;
  status: 'registration' | 'in_progress' | 'completed';
  bracket_data: any | null;
  created_at: string;
  started_at: string | null;
  completed_at: string | null;
  tournament_type: 'bracket' | 'round_robin';
  round_robin_data: any | null;
  award_winners?: any | null;
};

export type TournamentPlayerRow = {
  tournament_id: string;
  user_id: string;
  seed: number | null;
  placement: number | null;
  stats: any | null;
  joined_at: string;
};

export type UserPreferencesRow = {
  id: string;
  user_id: string;
  board_background_color: string;
  ball_color: string;
  private_mode: boolean;
  created_at: string;
  updated_at: string;
};

export class SupabaseRepo {
  async getUserByName(name: string) {
    const { data, error } = await sb
      .from('users')
      .select('*')
      .ilike('user_name', name)
      .maybeSingle();
    if (error && error.code !== 'PGRST116') throw error; // PGRST116 = no rows
    return data ?? null;
  }

  async getUserById(id: string) {
    const { data, error } = await sb
      .from('users')
      .select('*')
      .eq('id', id)
      .maybeSingle();
    if (error && error.code !== 'PGRST116') throw error; // PGRST116 = no rows
    return data ?? null;
  }

  async upsertUser(name: string): Promise<UserRow> {
    // insert or return existing by unique user_name
    const existing = await this.getUserByName(name);
    if (existing) return existing;
    const { data, error } = await sb
      .from('users')
      .insert({ user_name: name })
      .select('*')
      .single();
    if (error) throw error;
    return data;
  }

  async updateUser(u: Partial<UserRow> & { id: string }) {
    const { data, error } = await sb.from('users').update(u).eq('id', u.id).select('*').single();
    if (error) throw error;
    return data;
  }

  async addGame(g: Partial<GameRow>) {
    const { data, error } = await sb.from('games').upsert(g).select('*').single();
    if (error) throw error;
    return data;
  }

  async listGamesByUser(userId: string) {
    const { data, error } = await sb
      .from('games')
      .select('*')
      .or(`p1_user_id.eq.${userId},p2_user_id.eq.${userId}`)
      .order('time_stamp', { ascending: false });
    if (error) throw error;
    return data ?? [];
  }

  async listCompletedMultiplayerGamesByUser(userId: string) {
    // Archived definition: completed, multiplayer (non-solo), non-tournament, with opponent/user info
    const { data, error } = await sb
      .from('games')
      .select(`
        *,
        p1:p1_user_id ( user_name, profile_image_url ),
        p2:p2_user_id ( user_name, profile_image_url )
      `)
      .or(`p1_user_id.eq.${userId},p2_user_id.eq.${userId}`)
      .eq('status', 'completed')
      .eq('is_solo_game', 'false')
      .is('tournament_id', null)
      .order('updated_at', { ascending: false });
    if (error) throw error;    
    return data ?? [];
  }

  async diagnoseWinLossRecord(userId: string): Promise<void> {
    console.log(`\n[DIAGNOSE W-L] ===== Analyzing games for user ${userId} =====`);

    // Fetch ALL games for this user
    const allGames = await this.listGamesByUser(userId);
    console.log(`[DIAGNOSE W-L] Total games found: ${allGames.length}`);

    let wins = 0;
    let losses = 0;
    let ties = 0;
    let excludedCount = 0;
    const gameDetails: any[] = [];

    for (const game of allGames as any[]) {
      const isSolo = game.is_solo_game;
      const isTournament = !!game.tournament_id;
      const isAI = game.p1_user_id === 'ai_robo_roller' || game.p2_user_id === 'ai_robo_roller';
      const isCompletedOrForfeited = game.status === 'completed' || game.status === 'forfeited';

      // Should this game count toward W-L?
      const shouldCount = !isSolo && !isTournament && !isAI && isCompletedOrForfeited;

      if (!shouldCount) {
        excludedCount++;
        console.log(`[DIAGNOSE W-L] EXCLUDED: ${game.id} (solo:${isSolo}, tourney:${isTournament}, AI:${isAI}, status:${game.status})`);
        continue;
      }

      // Use winner_id as single source of truth
      let result = 'UNKNOWN';
      if (game.winner_id === userId) {
        wins++;
        result = game.status === 'forfeited' ? 'FORFEIT WIN' : 'WIN';
      } else if (game.winner_id) {
        losses++;
        result = game.status === 'forfeited' ? 'FORFEIT LOSS' : 'LOSS';
      } else {
        // No winner_id (shouldn't happen for new games)
        result = 'NO WINNER_ID';
      }

      // Build game details object
      const detail: any = {
        id: game.id,
        p1: game.p1_user_id,
        p2: game.p2_user_id,
        winnerId: game.winner_id,
        result,
        status: game.status,
        updated: game.updated_at
      };

      gameDetails.push(detail);
    }

    console.log(`\n[DIAGNOSE W-L] ===== CALCULATED W-L RECORD =====`);
    console.log(`[DIAGNOSE W-L] Wins: ${wins}`);
    console.log(`[DIAGNOSE W-L] Losses: ${losses}`);
    console.log(`[DIAGNOSE W-L] Ties: ${ties}`);
    console.log(`[DIAGNOSE W-L] Games counted: ${gameDetails.length}`);
    console.log(`[DIAGNOSE W-L] Games excluded: ${excludedCount}`);
    console.log(`\n[DIAGNOSE W-L] ===== GAME DETAILS =====`);
    gameDetails.forEach(g => {
      console.log(`[DIAGNOSE W-L] ${g.result}: Game ${g.id.substring(0, 8)}...`);
      console.log(`  - Regular: ${g.regularScores}, SuddenDeath: ${g.suddenDeathScores}`);
      console.log(`  - Used: ${g.finalScores} (suddenDeath: ${g.inSuddenDeath})`);
      console.log(`  - UserIsP1: ${g.userIsP1}, Updated: ${g.updated}`);
    });

    // Fetch actual DB record
    const userData = await this.getUserById(userId);
    console.log(`\n[DIAGNOSE W-L] ===== DB RECORD =====`);
    console.log(`[DIAGNOSE W-L] DB Wins: ${userData?.wins ?? 'N/A'}`);
    console.log(`[DIAGNOSE W-L] DB Losses: ${userData?.losses ?? 'N/A'}`);
    console.log(`[DIAGNOSE W-L] DB GamesPlayed: ${userData?.games_played ?? 'N/A'}`);
    console.log(`\n[DIAGNOSE W-L] ===== COMPARISON =====`);
    console.log(`[DIAGNOSE W-L] Calculated vs DB Wins: ${wins} vs ${userData?.wins ?? 'N/A'} ${wins === userData?.wins ? '✓' : '✗ MISMATCH'}`);
    console.log(`[DIAGNOSE W-L] Calculated vs DB Losses: ${losses} vs ${userData?.losses ?? 'N/A'} ${losses === userData?.losses ? '✓' : '✗ MISMATCH'}`);
    console.log(`[DIAGNOSE W-L] ==========================================\n`);
  }

  async calculateWinLoss(userId: string, options?: {
    excludeTournaments?: boolean;
    excludeAI?: boolean;
  }): Promise<{ wins: number; losses: number; gamesPlayed: number }> {    
    const games = await this.listGamesByUser(userId);    

    let wins = 0;
    let losses = 0;
    let gamesPlayed = 0;
    let excluded = 0;

    for (const game of games as any[]) {
      // Only count completed and forfeited games
      if (game.status !== 'completed' && game.status !== 'forfeited') {
        excluded++;
        continue;
      }

      // Exclude solo games
      if (game.is_solo_game) {
        excluded++;
        continue;
      }

      // Apply optional filters
      if (options?.excludeTournaments && game.tournament_id) {
        excluded++;
        continue;
      }
      if (options?.excludeAI && (game.p1_user_id === 'ai_robo_roller' || game.p2_user_id === 'ai_robo_roller')) {
        excluded++;
        continue;
      }

      gamesPlayed++;

      // Use winner_id as single source of truth for all games
      if (game.winner_id === userId) {
        wins++;
      } else if (game.winner_id) {
        // winner_id exists but it's not the current user = loss
        losses++;
      }
      // Note: winner_id should always be set for completed/forfeited games
      // If it's missing, we skip counting it as win/loss (shouldn't happen in new games)
    }
    
    return { wins, losses, gamesPlayed };
  }

  async getUserRollHistories(userId: string): Promise<number[]> {
    // Efficiently fetch only roll histories for PPR calculation
    // Excludes solo games and tournaments
    const { data, error } = await sb
      .from('games')
      .select('p1_user_id, p2_user_id, p1_roll_history, p2_roll_history')
      .or(`p1_user_id.eq.${userId},p2_user_id.eq.${userId}`)
      .eq('is_solo_game', false)
      .is('tournament_id', null)
      .in('status', ['active', 'completed']);

    if (error) {
      console.error('Error fetching roll histories:', error);
      return [];
    }

    const rolls: number[] = [];
    (data || []).forEach((g: any) => {
      const hist = (g.p1_user_id === userId) ? g.p1_roll_history : g.p2_roll_history;
      if (Array.isArray(hist)) {
        for (const v of hist) {
          const n = typeof v === 'number' ? v : (typeof v === 'string' ? Number(v) : NaN);
          if (Number.isFinite(n)) rolls.push(n);
        }
      }
    });

    return rolls;
  }

  async calculateUserPPR(userId: string): Promise<number> {
    // Official PPR: multiplayer games only (active + completed), exclude solo and tournaments
    const rolls = await this.getUserRollHistories(userId);
    if (rolls.length === 0) return 0;
    const sum = rolls.reduce((a, b) => a + b, 0);
    return +(sum / rolls.length).toFixed(3);
  }

  async listActiveGamesByUser(userId: string) {
    const { data, error } = await sb
      .from('games')
      .select(`
        *,
        p1:p1_user_id ( user_name, profile_image_url ),
        p2:p2_user_id ( user_name, profile_image_url )
      `)
      .or(`p1_user_id.eq.${userId},p2_user_id.eq.${userId}`)
      .in('status', ['active', 'forfeited'])
      .eq('is_solo_game', 'false')
      .order('updated_at', { ascending: false });

    if (error) {
      console.error('Error listing active games:', error);
      throw error;
    }
    return data ?? [];
  }

  async getActiveGame(userId: string): Promise<GameRow | null> {
    const { data, error } = await sb
      .from('games')
      .select('*')
      .or(`p1_user_id.eq.${userId},p2_user_id.eq.${userId}`)
      .eq('status', 'active')
      .eq('is_solo_game', 'false')
      .order('updated_at', { ascending: false })
      .limit(1)
      .maybeSingle();

    if (error && error.code !== 'PGRST116') throw error; // PGRST116 = no rows
    return data ?? null;
  }

  async hasUnfinishedGame(userId: string): Promise<boolean> {
    const game = await this.getActiveGame(userId);
    return game !== null;
  }

  async getUnfinishedGameById(gameId: string): Promise<GameRow | null> {
    const { data, error } = await sb
      .from('games')
      .select('*')
      .eq('is_solo_game', false)
      .eq('id', gameId)
      .eq('status', 'active')
      .single();

    if (error && error.code !== 'PGRST116') { // PGRST116 = no rows found
      console.error('Error fetching unfinished game by ID:', error);
      return null;
    }
    return data;
  }

  async forfeitGame(gameId: string, forfeiterUserId: string): Promise<void> {
    // First, fetch the game to determine the winner
    const { data: game, error: fetchError } = await sb
      .from('games')
      .select('p1_user_id, p2_user_id')
      .eq('id', gameId)
      .single();

    if (fetchError || !game) {
      console.error('DATABASE ERROR fetching game for forfeit:', fetchError);
      throw fetchError || new Error('Game not found');
    }

    // Determine winner (the player who didn't forfeit)
    const winnerId = game.p1_user_id === forfeiterUserId ? game.p2_user_id : game.p1_user_id;

    console.log(`[forfeitGame] Game ${gameId}: Forfeiter=${forfeiterUserId}, Winner=${winnerId}`);

    // Update game with forfeited status and winner
    const { data, error } = await sb
      .from('games')
      .update({
        status: 'forfeited',
        winner_id: winnerId
      })
      .eq('id', gameId)
      .select();

    if (error) {
      console.error('DATABASE ERROR forfeiting game:', error);
      throw error; // Make the error impossible to ignore
    }

  }

  async createChannel(channelName: string, presenceKey: string) {
    return sb.channel(channelName, {
          config: { presence: { key: presenceKey } } });
  }

  // User preferences methods
  async getUserPreferences(userId: string): Promise<UserPreferencesRow | null> {
    const { data, error } = await sb
      .from('user_preferences')
      .select('*')
      .eq('user_id', userId)
      .maybeSingle();
    if (error && error.code !== 'PGRST116') throw error; // PGRST116 = no rows
    return data ?? null;
  }

  async upsertUserPreferences(userId: string, prefs: Partial<Omit<UserPreferencesRow, 'id' | 'user_id' | 'created_at' | 'updated_at'>>): Promise<UserPreferencesRow> {
    const { data, error } = await sb
      .from('user_preferences')
      .upsert({
        user_id: userId,
        ...prefs
      }, {
        onConflict: 'user_id'
      })
      .select('*')
      .single();
    if (error) throw error;
    return data;
  }

  async updateUserPreferences(userId: string, prefs: Partial<Omit<UserPreferencesRow, 'id' | 'user_id' | 'created_at' | 'updated_at'>>): Promise<UserPreferencesRow> {
    const { data, error } = await sb
      .from('user_preferences')
      .update(prefs)
      .eq('user_id', userId)
      .select('*')
      .single();
    if (error) throw error;
    return data;
  }

  async updateGame(gameId: string, gameData: Partial<GameRow>) {
    const { data, error } = await sb
        .from('games')
        .update(gameData)
        .eq('id', gameId)
        .select()
        .single();
    if (error) throw error;
    return data;
  }

  async getGameById(gameId: string): Promise<any | null> {
    const { data, error } = await sb.from('games').select('*').eq('id', gameId).maybeSingle();
    if (error && error.code !== 'PGRST116') throw error;
    return data ?? null;
  }

  async getTournamentRollHistories(tournamentId: string, userId: string): Promise<number[]> {
    // Efficiently fetch only roll histories for tournament PPR calculation
    const { data, error } = await sb
      .from('games')
      .select('p1_user_id, p2_user_id, p1_roll_history, p2_roll_history')
      .eq('tournament_id', tournamentId)
      .or(`p1_user_id.eq.${userId},p2_user_id.eq.${userId}`)
      .in('status', ['active', 'completed']);

    if (error) {
      console.error('Error fetching tournament roll histories:', error);
      return [];
    }

    const rolls: number[] = [];
    (data || []).forEach((g: any) => {
      const hist = (g.p1_user_id === userId) ? g.p1_roll_history : g.p2_roll_history;
      if (Array.isArray(hist)) {
        for (const v of hist) {
          const n = typeof v === 'number' ? v : (typeof v === 'string' ? Number(v) : NaN);
          if (Number.isFinite(n)) rolls.push(n);
        }
      }
    });

    return rolls;
  }

  async calculateUserPPRForTournament(tournamentId: string, userId: string): Promise<number> {
    // Use the roll histories method for consistency
    const rolls = await this.getTournamentRollHistories(tournamentId, userId);
    if (rolls.length === 0) return 0;
    const sum = rolls.reduce((a, b) => a + b, 0);
    return +(sum / rolls.length).toFixed(3);
  }

  async findLatestGameBetweenUsers(userAId: string, userBId: string): Promise<GameRow | null> {
    // Find the most recent game between the two users regardless of player order
    const { data, error } = await sb
      .from('games')
      .select('*')
      .or(
        `and(p1_user_id.eq.${userAId},p2_user_id.eq.${userBId}),and(p1_user_id.eq.${userBId},p2_user_id.eq.${userAId})`
      )
      .in('status', ['active', 'completed'])
      .order('updated_at', { ascending: false })
      .limit(1)
      .maybeSingle();
    if (error && error.code !== 'PGRST116') throw error;
    return (data as any) ?? null;
  }

  async findTournamentGame(userAId: string, userBId: string, tournamentId: string, round: number, matchIndex: number): Promise<GameRow | null> {
    // Find game between two users for specific tournament match
    const { data, error } = await sb
      .from('games')
      .select('*')
      .or(
        `and(p1_user_id.eq.${userAId},p2_user_id.eq.${userBId}),and(p1_user_id.eq.${userBId},p2_user_id.eq.${userAId})`
      )
      .eq('tournament_id', tournamentId)
      .eq('tournament_round', round)
      .eq('tournament_match', matchIndex)
      .in('status', ['active', 'completed'])
      .order('updated_at', { ascending: false })
      .limit(1)
      .maybeSingle();
    if (error && error.code !== 'PGRST116') throw error;
    return (data as any) ?? null;
  }

  // Tournaments
  async createTournament(params: { name: string; creatorId: string; maxPlayers?: number; joinCode: string; type?: 'bracket' | 'round_robin' }): Promise<TournamentRow> {
    const { name, creatorId, joinCode, type } = params;
    const maxPlayers = params.maxPlayers ?? 16;
    const { data, error } = await sb
      .from('tournaments')
      .insert({ name, creator_id: creatorId, join_code: joinCode, max_players: maxPlayers, status: 'registration', tournament_type: type ?? 'bracket', round_robin_data: null })
      .select('*')
      .single();
    if (error) throw error;
    // Ensure creator is enrolled as a player (seed 1)
    try {
      await sb
        .from('tournament_players')
        .insert({ tournament_id: (data as any).id, user_id: creatorId, seed: 1 })
        .select('*')
        .single();
    } catch (e) {
      // ignore if already exists or any benign error; host can still proceed
    }
    return data as TournamentRow;
  }

  async getTournamentByJoinCode(joinCode: string): Promise<TournamentRow | null> {
    const { data, error } = await sb
      .from('tournaments')
      .select('*')
      .eq('join_code', joinCode)
      .maybeSingle();
    if (error && error.code !== 'PGRST116') throw error;
    return (data as TournamentRow) ?? null;
  }

  async joinTournamentByCode(userId: string, joinCode: string): Promise<{ tournament: TournamentRow; player: TournamentPlayerRow }> {
    const t = await this.getTournamentByJoinCode(joinCode);
    if (!t) throw new Error('Tournament not found');
    if (t.status !== 'registration') throw new Error('Tournament is not open');

    // If already a member, return without inserting
    const { data: existing, error: existErr } = await sb
      .from('tournament_players')
      .select('*')
      .eq('tournament_id', t.id)
      .eq('user_id', userId)
      .maybeSingle();
    if (existErr && existErr.code !== 'PGRST116') throw existErr;
    if (existing) {
      return { tournament: t, player: existing as unknown as TournamentPlayerRow };
    }

    // Find next seed (simple strategy; small race risk acceptable for now)
    const { data: maxRow, error: maxErr } = await sb
      .from('tournament_players')
      .select('seed')
      .eq('tournament_id', t.id)
      .order('seed', { ascending: false })
      .limit(1)
      .maybeSingle();
    if (maxErr && maxErr.code !== 'PGRST116') throw maxErr;
    const nextSeed = ((maxRow as any)?.seed ?? 0) + 1;

    // Insert player
    const { data: player, error: insErr } = await sb
      .from('tournament_players')
      .insert({ tournament_id: t.id, user_id: userId, seed: nextSeed })
      .select('*')
      .single();
    if (insErr) throw insErr;
    return { tournament: t, player: player as unknown as TournamentPlayerRow };
  }

  async getTournamentById(id: string): Promise<TournamentRow | null> {
    const { data, error } = await sb.from('tournaments').select('*').eq('id', id).maybeSingle();
    if (error && error.code !== 'PGRST116') throw error;
    return (data as TournamentRow) ?? null;
  }

  /**
   * Get active tournament match game details for observer mode
   * Returns matchId, gameId, and player details if match is in progress
   */
  async getTournamentMatchGame(
    tournamentId: string,
    round: number,
    matchIndex: number
  ): Promise<{ gameId: string; matchId: string; player1: IUser; player2: IUser } | null> {
    // Query games table for in-progress game matching tournament criteria
    const { data: games, error: gamesError } = await sb
      .from('games')
      .select('id, p1_user_id, p2_user_id, status')
      .eq('tournament_id', tournamentId)
      .eq('tournament_round', round)
      .eq('tournament_match', matchIndex)
      .eq('status', 'active')
      .maybeSingle();

    if (gamesError && gamesError.code !== 'PGRST116') throw gamesError;
    if (!games) return null; // No active game for this match

    // Fetch both players' full user data (raw rows)
    const p1 = await this.getUserById(games.p1_user_id);
    const p2 = await this.getUserById(games.p2_user_id);

    if (!p1 || !p2) {
      console.error('[getTournamentMatchGame] Could not fetch player data');
      return null;
    }

    // Map DB rows -> IUser shape used by game/renderer
    const mapToIUser = (u: any): IUser => ({
      id: u.id,
      userName: u.user_name,
      // Note: wins, losses, gamesPlayed columns were removed in migration 0006
      // For tournament games: these will be populated from tournamentPlayerRecords (round robin) or hidden (bracket)
      // For non-tournament games: these will be calculated via calculateWinLoss() in index.tsx
      numOfSkeeches: u.num_of_skeeches ?? 0,
      pointsTotal: u.points_total ?? 0,
      rollsTotal: u.rolls_total ?? 0,
      roundsTotal: u.rounds_total ?? 0,
      avgPPR: Number(u.avg_ppr ?? 0),
      createdAt: u.created_at ? Date.parse(u.created_at) : Date.now(),
      updatedAt: u.updated_at ? Date.parse(u.updated_at) : Date.now(),
      profile_image_url: u.profile_image_url ?? undefined,
      current_game_rolls: []
    });

    const player1 = mapToIUser(p1);
    const player2 = mapToIUser(p2);

    return {
      gameId: games.id,
      matchId: games.id, // Use game ID as match ID
      player1,
      player2,
    };
  }

  async listTournamentPlayers(tournamentId: string): Promise<Array<{ user_id: string; seed: number | null; stats?: any }>> {
    const { data, error } = await sb
      .from('tournament_players')
      .select('user_id, seed, stats')
      .eq('tournament_id', tournamentId)
      .order('seed', { ascending: true });
    if (error) throw error;
    return (data as any[]) ?? [];
  }

  async leaveTournament(tournamentId: string, userId: string): Promise<void> {
    const { error } = await sb
      .from('tournament_players')
      .delete()
      .eq('tournament_id', tournamentId)
      .eq('user_id', userId);
    if (error) throw error;
  }

  async startTournament(tournamentId: string): Promise<void> {
    const { error } = await sb
      .from('tournaments')
      .update({ status: 'in_progress', started_at: new Date().toISOString() })
      .eq('id', tournamentId);
    if (error) throw error;
  }

  // Bracket generation and tournament start (creator only)
  async startTournamentWithBracket(tournamentId: string): Promise<any> {
    // Load players with seeds
    const players = await this.listTournamentPlayers(tournamentId);
    // Sort by seed ascending
    players.sort((a, b) => (a.seed ?? 0) - (b.seed ?? 0));
    const seeded = players.filter(p => (p.seed ?? 0) > 0);
    const playerCount = seeded.length;
    const nextPow2 = (n: number) => {
      if (n <= 1) return 1;
      let p = 1;
      while (p < n) p <<= 1;
      return p;
    };
    const bracketSize = nextPow2(playerCount || 1);
    const numByes = bracketSize - playerCount;
    // Build seed to user map (1-based seeds)
    const seedMap: Record<number, string | null> = {};
    for (let i = 1; i <= bracketSize; i++) seedMap[i] = null;
    for (const p of seeded) {
      if (p.seed && p.seed >= 1 && p.seed <= bracketSize) seedMap[p.seed] = p.user_id;
    }
    // First round pairing: standard bracket ordering to separate top seeds into opposite halves.
    // Build seed order top-to-bottom (e.g., for 8: [1,8,4,5,2,7,3,6])
    const buildSeedOrder = (n: number): number[] => {
      if (n <= 1) return [1];
      let order: number[] = [1, 2];
      let m = 2;
      while (m < n) {
        m *= 2; // expand to next power-of-two stage
        const next: number[] = [];
        for (const s of order) {
          next.push(s);
          next.push(m + 1 - s);
        }
        order = next;
      }
      return order.slice(0, n);
    };

    const seedOrder = buildSeedOrder(bracketSize);
    const firstRound: any[] = [];
    const autoAdvances: Array<{ seed: number; user_id: string }> = [];
    for (let idx = 0; idx < seedOrder.length; idx += 2) {
      const aSeed = seedOrder[idx];
      const bSeed = seedOrder[idx + 1];
      const aUser = seedMap[aSeed];
      const bUser = seedMap[bSeed];
      const isBye = (!!aUser && !bUser) || (!aUser && !!bUser);
      let winnerSeed: number | null = null;
      if (isBye) {
        winnerSeed = aUser ? aSeed : bSeed;
        const advUser = (aUser || bUser)!;
        autoAdvances.push({ seed: winnerSeed, user_id: advUser });
      }
      firstRound.push({
        matchIndex: idx / 2 + 1,
        a: { seed: aSeed, user_id: aUser },
        b: { seed: bSeed, user_id: bUser },
        isBye,
        winnerSeed,
        result: null,
      });
    }
    // Build rounds structure skeleton
    const totalRounds = Math.log2(bracketSize) | 0;
    const rounds: any[] = [];
    rounds.push({ round: 1, matches: firstRound });
    for (let r = 2; r <= totalRounds; r++) {
      const matchesInRound = bracketSize / Math.pow(2, r);
      const matches: any[] = [];
      for (let m = 1; m <= matchesInRound; m++) {
        matches.push({ matchIndex: m, aFrom: { round: r - 1, matchIndex: (m - 1) * 2 + 1 }, bFrom: { round: r - 1, matchIndex: (m - 1) * 2 + 2 }, result: null });
      }
      rounds.push({ round: r, matches });
    }
    const bracketData = {
      bracketSize,
      playerCount,
      numByes,
      rounds,
      autoAdvances,
      generatedAt: new Date().toISOString(),
    };
    // Update tournament with bracket_data and status/start time
    const { data, error } = await sb
      .from('tournaments')
      .update({ status: 'in_progress', started_at: new Date().toISOString(), bracket_data: bracketData })
      .eq('id', tournamentId)
      .select('*')
      .single();
    if (error) throw error;
    return data;
  }

  async startRoundRobinTournament(tournamentId: string): Promise<any> {
    const players = await this.listTournamentPlayers(tournamentId);
    if (players.length < 2) throw new Error('Need at least 2 players for round-robin');

    const roster = normalizeRoundRobinPlayers(players);
    const schedule = generateRoundRobinSchedule(roster);
    const totalMatches = countActualRoundRobinMatches(schedule);

    const standings = roster.map(p => ({
      userId: p.userId,
      wins: 0,
      losses: 0,
      pointsFor: 0,
      pointsAgainst: 0,
      pointDifferential: 0,
      matchesPlayed: 0,
      rank: 0
    }));

    const roundRobinData = {
      schedule,
      standings,
      tiebreakers: DEFAULT_TIEBREAKERS,
      generatedAt: new Date().toISOString(),
      completedMatchCount: 0,
      totalMatchCount: totalMatches
    };

    const { data, error } = await sb
      .from('tournaments')
      .update({
        status: 'in_progress',
        started_at: new Date().toISOString(),
        round_robin_data: roundRobinData
      })
      .eq('id', tournamentId)
      .select('*')
      .single();
    if (error) throw error;
    return data;
  }

  async recordRoundRobinMatchResult(params: {
    tournamentId: string;
    matchIndex: number;
    aScore: number;
    bScore: number;
    winnerUserId: string;
    playerAPoints?: number;
    playerBPoints?: number;
  }): Promise<any> {
    const { tournamentId, matchIndex, aScore, bScore, winnerUserId } = params;

    console.log('[recordRoundRobinMatchResult] Recording match result:', {
      matchIndex,
      aScore,
      bScore,
      winnerUserId
    });

    const t = await this.getTournamentById(tournamentId);
    if (!t || !t.round_robin_data) throw new Error('Round-robin tournament not found');

    const rrData: any = JSON.parse(JSON.stringify(t.round_robin_data));
    const schedule = Array.isArray(rrData.schedule) ? rrData.schedule : [];
    const match = schedule.find((m: any) => m.matchIndex === matchIndex);
    if (!match) throw new Error('Match not found in round-robin schedule');

    console.log('[recordRoundRobinMatchResult] Found match:', {
      matchIndex: match.matchIndex,
      round: match.round,
      playerA: { id: match.playerA?.userId, seed: match.playerA?.seed },
      playerB: { id: match.playerB?.userId, seed: match.playerB?.seed }
    });
    if (match.playerA?.userId === BYE_USER_ID || match.playerB?.userId === BYE_USER_ID) {
      throw new Error('Cannot record a BYE match');
    }
    if (match.result) throw new Error('Match already recorded');

    const makeSafeNumber = (value: any) => (typeof value === 'number' && Number.isFinite(value) ? value : undefined);
    const result: any = {
      aScore,
      bScore,
      winnerUserId,
      completedAt: new Date().toISOString()
    };
    const safePlayerAPoints = makeSafeNumber(params.playerAPoints);
    const safePlayerBPoints = makeSafeNumber(params.playerBPoints);
    if (safePlayerAPoints !== undefined) result.playerAPoints = safePlayerAPoints;
    if (safePlayerBPoints !== undefined) result.playerBPoints = safePlayerBPoints;
    match.result = result;

    const totalMatches = rrData.totalMatchCount ?? countActualRoundRobinMatches(schedule);
    rrData.totalMatchCount = totalMatches;
    rrData.completedMatchCount = (rrData.completedMatchCount ?? 0) + 1;

    const roster = normalizeRoundRobinPlayers(await this.listTournamentPlayers(tournamentId));
    rrData.standings = calculateStandings(
      roster,
      schedule,
      rrData.tiebreakers ?? DEFAULT_TIEBREAKERS
    );

    const isComplete = rrData.completedMatchCount >= totalMatches;
    const updatePayload: any = { round_robin_data: rrData };
    if (isComplete) {
      updatePayload.status = 'completed';
      updatePayload.completed_at = new Date().toISOString();
    }

    const { data, error } = await sb
      .from('tournaments')
      .update(updatePayload)
      .eq('id', tournamentId)
      .select('*')
      .single();
    if (error) throw error;

    if (isComplete && Array.isArray(rrData.standings)) {
      for (const standing of rrData.standings) {
        await sb
          .from('tournament_players')
          .update({ placement: standing.rank })
          .eq('tournament_id', tournamentId)
          .eq('user_id', standing.userId);
      }
    }

    return data;
  }

  /**
   * TEMP DEBUG: Calculate standings from actual roll histories in the games table
   * This allows us to compare with playerAPoints/playerBPoints to verify data accuracy
   */
  async calculateStandingsFromRollHistories(
    tournamentId: string,
    matches: Array<{ playerA: { userId: string }; playerB: { userId: string }; result?: any }>
  ): Promise<{
    standingsFromRolls: Map<string, { pointsFor: number; pointsAgainst: number }>;
    matchDebug: Array<{
      matchIndex: number;
      playerA: string;
      playerB: string;
      playerAPointsStored?: number;
      playerBPointsStored?: number;
      playerAPointsFromRolls: number;
      playerBPointsFromRolls: number;
      match: string;
    }>;
  }> {
    const standingsFromRolls = new Map<string, { pointsFor: number; pointsAgainst: number }>();

    // Initialize entries for all players
    for (const match of matches) {
      if (!standingsFromRolls.has(match.playerA.userId)) {
        standingsFromRolls.set(match.playerA.userId, { pointsFor: 0, pointsAgainst: 0 });
      }
      if (!standingsFromRolls.has(match.playerB.userId)) {
        standingsFromRolls.set(match.playerB.userId, { pointsFor: 0, pointsAgainst: 0 });
      }
    }

    const matchDebug = [];

    // For each completed match, fetch the game row and sum roll histories
    for (let i = 0; i < matches.length; i++) {
      const match = matches[i];
      if (!match.result) continue; // Skip incomplete matches

      const playerAId = match.playerA.userId;
      const playerBId = match.playerB.userId;
      const gameId = `${tournamentId}:${(match as any).round}:${(match as any).matchIndex}`;

      // Try to find the game row for this tournament match
      const { data: games, error } = await sb
        .from('games')
        .select('p1_user_id,p2_user_id,p1_roll_history,p2_roll_history')
        .eq('tournament_id', tournamentId)
        .or(`and(p1_user_id.eq.${playerAId},p2_user_id.eq.${playerBId}),and(p1_user_id.eq.${playerBId},p2_user_id.eq.${playerAId})`)
        .order('created_at', { ascending: false })
        .limit(1);

      if (error || !games || games.length === 0) {
        console.warn(`[calculateStandingsFromRollHistories] No game row found for match between ${playerAId} and ${playerBId}`);
        continue;
      }

      const game = games[0];
      const p1Id = game.p1_user_id;
      const p1Rolls = game.p1_roll_history || [];
      const p2Rolls = game.p2_roll_history || [];

      const p1PointsFromRolls = p1Rolls.reduce((sum: number, r: number) => sum + r, 0);
      const p2PointsFromRolls = p2Rolls.reduce((sum: number, r: number) => sum + r, 0);

      // Map to player A/B slots
      const playerAPointsFromRolls = p1Id === playerAId ? p1PointsFromRolls : p2PointsFromRolls;
      const playerBPointsFromRolls = p1Id === playerAId ? p2PointsFromRolls : p1PointsFromRolls;

      // Get stored playerAPoints/playerBPoints if available
      const playerAPointsStored = match.result?.playerAPoints;
      const playerBPointsStored = match.result?.playerBPoints;

      // Update standings from roll totals
      const aEntry = standingsFromRolls.get(playerAId);
      const bEntry = standingsFromRolls.get(playerBId);
      if (aEntry && bEntry) {
        aEntry.pointsFor += playerAPointsFromRolls;
        aEntry.pointsAgainst += playerBPointsFromRolls;
        bEntry.pointsFor += playerBPointsFromRolls;
        bEntry.pointsAgainst += playerAPointsFromRolls;
      }

      matchDebug.push({
        matchIndex: (match as any).matchIndex ?? i,
        playerA: playerAId,
        playerB: playerBId,
        playerAPointsStored,
        playerBPointsStored,
        playerAPointsFromRolls,
        playerBPointsFromRolls,
        match: `${playerAId} vs ${playerBId}`
      });
    }

    return { standingsFromRolls, matchDebug };
  }

  async listUserTournaments(userId: string): Promise<TournamentRow[]> {
    const { data, error } = await sb
      .from('tournament_players')
      .select('tournament_id, tournaments!inner(*)')
      .eq('user_id', userId)
      .order('joined_at', { ascending: false });
    if (error) throw error;
    // data will be array of rows with tournaments object
    const rows = (data as any[]) ?? [];
    const fromMembership = rows.map(r => r.tournaments) as TournamentRow[];
    // Also include tournaments the user created (host), in case they aren't yet added to players
    const { data: created, error: err2 } = await sb
      .from('tournaments')
      .select('*')
      .eq('creator_id', userId)
      .order('created_at', { ascending: false });
    if (err2) throw err2;
    const combined = [...fromMembership, ...((created as TournamentRow[]) || [])];
    // De-duplicate by id
    const seen = new Set<string>();
    const uniq: TournamentRow[] = [];
    for (const t of combined) {
      if (!t || !t.id) continue;
      if (seen.has(t.id)) continue;
      seen.add(t.id);
      uniq.push(t);
    }
    return uniq;
  }

  // Update bracket on match completion and advance winner to next round
  async recordTournamentMatchResult(params: { tournamentId: string; round: number; matchIndex: number; aScore: number; bScore: number; winnerUserId: string }): Promise<any> {
    const { tournamentId, round, matchIndex, aScore, bScore, winnerUserId } = params;
    const t = await this.getTournamentById(tournamentId);
    if (!t || !t.bracket_data) throw new Error('Tournament/bracket not found');
    const bd: any = t.bracket_data;
    const rIdx = round - 1;
    const mIdx = matchIndex - 1;
    if (!bd.rounds?.[rIdx]?.matches?.[mIdx]) throw new Error('Match not found in bracket');

    const match = bd.rounds[rIdx].matches[mIdx];
    // Determine which bracket side (A/B) corresponds to the actual winner user id
    let winnerSide: 'a' | 'b' | null = null;
    if (match.a?.user_id === winnerUserId) winnerSide = 'a';
    else if (match.b?.user_id === winnerUserId) winnerSide = 'b';

    // Fallback: if winnerUserId doesn't match either side (shouldn't happen), decide by scores
    if (!winnerSide) winnerSide = (aScore >= bScore) ? 'a' : 'b';

    // Map scores into bracket A/B order using winner side to place the higher score
    const hi = Math.max(aScore, bScore);
    const lo = Math.min(aScore, bScore);
    match.result = winnerSide === 'a' ? { aScore: hi, bScore: lo } : { aScore: lo, bScore: hi };

    // Winner seed/user from bracket A/B
    const winner = match[winnerSide];
    const advUserId = winner?.user_id || null;
    const winnerSeed = winner?.seed;
    if (!advUserId || !winnerSeed) throw new Error('Unable to determine winner from match');

    // If there is a next round, place winner
    const nextRoundNum = round + 1;
    const nextRIdx = rIdx + 1;
    if (bd.rounds[nextRIdx]) {
      const nextMatchIndex = Math.ceil(matchIndex / 2);
      const nextMIdx = nextMatchIndex - 1;
      const nextMatch = bd.rounds[nextRIdx].matches[nextMIdx];
      // Ensure provenance pointers are set for layout (sources are 2k-1 and 2k for next round match k)
      if (!nextMatch.aFrom) nextMatch.aFrom = { round, matchIndex: nextMatchIndex * 2 - 1 };
      if (!nextMatch.bFrom) nextMatch.bFrom = { round, matchIndex: nextMatchIndex * 2 };
      const isAFrom = (matchIndex % 2) === 1; // odd feeds into aFrom by convention
      const slotKey = isAFrom ? 'a' : 'b';
      nextMatch[slotKey] = { seed: winnerSeed, user_id: advUserId };
    } else {
      // No next round -> tournament completed
      bd.completedAt = new Date().toISOString();
    }

    const update: any = { bracket_data: bd };
    if (!bd.rounds[nextRIdx]) {
      update.status = 'completed';
      update.completed_at = new Date().toISOString();
    }

    const { data, error } = await sb.from('tournaments').update(update).eq('id', tournamentId).select('*').single();
    if (error) throw error;
    return data;
  }

  // Apply auto-advance for bye winners (optional utility)
  async applyBracketAutoAdvances(tournamentId: string): Promise<any> {
    const t = await this.getTournamentById(tournamentId);
    if (!t || !t.bracket_data) throw new Error('Tournament/bracket not found');
    const bd: any = t.bracket_data;
    if (!bd.autoAdvances || !Array.isArray(bd.autoAdvances)) return t;
    const firstRound = bd.rounds?.[0];
    if (!firstRound) return t;

    // For each auto advance seed, propagate into next round slot
    bd.autoAdvances.forEach((adv: any) => {
      // Find match in round 1 containing this seed
      const mIdx = firstRound.matches.findIndex((m: any) => m.a?.seed === adv.seed || m.b?.seed === adv.seed);
      if (mIdx === -1) return;
      const matchIndex = mIdx + 1;
      const nextRIdx = 1; // round 2 index
      const nextMatchIndex = Math.ceil(matchIndex / 2);
      const nextMIdx = nextMatchIndex - 1;
      const nextMatch = bd.rounds[nextRIdx]?.matches?.[nextMIdx];
      if (!nextMatch) return;
      const isAFrom = (matchIndex % 2) === 1;
      const slotKey = isAFrom ? 'a' : 'b';
      nextMatch[slotKey] = { seed: adv.seed, user_id: adv.user_id };
    });
    // Clear autoAdvances once applied
    bd.autoAdvances = [];
    const { data, error } = await sb.from('tournaments').update({ bracket_data: bd }).eq('id', tournamentId).select('*').single();
    if (error) throw error;
    return data;
  }

  // --- Tournament player stats (awards) ---
  async getTournamentPlayer(tournamentId: string, userId: string) {
    const { data, error } = await sb
      .from('tournament_players')
      .select('*')
      .eq('tournament_id', tournamentId)
      .eq('user_id', userId)
      .maybeSingle();
    if (error && error.code !== 'PGRST116') throw error;
    return data ?? null;
  }

  async upsertTournamentPlayerStats(tournamentId: string, userId: string, delta: any) {
    // Fetch existing row
    const row = await this.getTournamentPlayer(tournamentId, userId);
    if (!row) return null;
    const current = row.stats || {};
    const merged = this.mergeTournamentStats(current, delta);
    const { data, error } = await sb
      .from('tournament_players')
      .update({ stats: merged })
      .eq('tournament_id', tournamentId)
      .eq('user_id', userId)
      .select('*')
      .single();
    if (error) throw error;
    return data;
  }

  async getUserTournamentParticipations(userId: string) {
    const selectWithAwards = `
        tournament_id,
        stats,
        tournaments (
          id,
          name,
          status,
          completed_at,
          award_winners
        )
      `;
    const selectWithoutAwards = `
        tournament_id,
        stats,
        tournaments (
          id,
          name,
          status,
          completed_at
        )
      `;

    // First try selecting award_winners; if column doesn't exist (old schema), fall back
    let { data, error }: { data: any; error: any } = await sb
      .from('tournament_players')
      .select(selectWithAwards)
      .eq('user_id', userId)
      .order('tournaments(completed_at)', { ascending: false });

    if (error && error.code === '42703') {
      // Column missing, retry without it
      const retry: { data: any; error: any } = await sb
        .from('tournament_players')
        .select(selectWithoutAwards)
        .eq('user_id', userId)
        .order('tournaments(completed_at)', { ascending: false });
      data = retry.data;
      error = retry.error;
    }

    if (error) throw error;
    return data || [];
  }

  async getTournamentPlayersWithStats(tournamentId: string) {
    const { data, error } = await sb
      .from('tournament_players')
      .select('user_id, stats')
      .eq('tournament_id', tournamentId);
    if (error) throw error;
    return data || [];
  }

  async saveTournamentAwardWinners(tournamentId: string, awards: any) {
    const { error } = await sb
      .from('tournaments')
      .update({ award_winners: awards })
      .eq('id', tournamentId);
    if (error) throw error;
  }

  private mergeTournamentStats(a: any, b: any) {
    const out: any = { ...a };
    const addKeys = ['busts','skeechCount','totalPoints','totalRolls','popUps'];
    const maxKeys = ['highestTurnScore','longestNoDitchStreak','fastestRoll','orbits'];
    // Sums
    for (const k of addKeys) {
      const av = Number(a?.[k] || 0);
      const bv = Number(b?.[k] || 0);
      if (av || bv) out[k] = av + bv;
    }
    // Maxes
    for (const k of maxKeys) {
      const av = a?.[k];
      const bv = b?.[k];
      if (av == null) out[k] = bv;
      else if (bv == null) out[k] = av;
      else out[k] = Math.max(av, bv);
    }
    // slowestRoll: min positive non-zero
    const slowA = a?.slowestRoll; const slowB = b?.slowestRoll;
    const pickMin = (x:any,y:any) => {
      const vx = (typeof x === 'number' && x>0) ? x : Infinity;
      const vy = (typeof y === 'number' && y>0) ? y : Infinity;
      const v = Math.min(vx, vy);
      return v === Infinity ? undefined : v;
    };
    const slow = pickMin(slowA, slowB);
    if (slow !== undefined) out.slowestRoll = slow;
    // Merge non-numeric/custom flags from b by preference (e.g., hasSeenAwardsShow)
    // Do not overwrite computed numeric aggregates handled above.
    if (b && typeof b === 'object') {
      for (const k of Object.keys(b)) {
        if (addKeys.includes(k) || maxKeys.includes(k) || k === 'slowestRoll') continue;
        // Prefer b's value for miscellaneous flags/strings
        out[k] = b[k];
      }
    }
    return out;
  }
}

function normalizeRoundRobinPlayers(
  players: Array<{ user_id: string; seed: number | null | undefined }>
): RoundRobinPlayer[] {
  const sorted = [...players].sort((a, b) => {
    const aSeed = typeof a.seed === 'number' ? a.seed : Number.MAX_SAFE_INTEGER;
    const bSeed = typeof b.seed === 'number' ? b.seed : Number.MAX_SAFE_INTEGER;
    if (aSeed !== bSeed) return aSeed - bSeed;
    return a.user_id.localeCompare(b.user_id);
  });

  let nextSeed = Math.max(0, ...players.map(p => (typeof p.seed === 'number' ? p.seed : 0)));
  if (!Number.isFinite(nextSeed)) nextSeed = 0;

  const normalized: RoundRobinPlayer[] = [];
  for (const player of sorted) {
    let seed = (typeof player.seed === 'number' && player.seed > 0) ? player.seed : 0;
    if (!seed) {
      nextSeed += 1;
      seed = nextSeed;
    }
    normalized.push({ userId: player.user_id, seed });
  }
  return normalized;
}

function countActualRoundRobinMatches(schedule: Array<any>): number {
  if (!Array.isArray(schedule)) return 0;
  return schedule.filter(m =>
    m?.playerA?.userId !== BYE_USER_ID && m?.playerB?.userId !== BYE_USER_ID
  ).length;
}


